import { inject, injectable, named } from 'inversify';
import { isFuture, isPast } from 'date-fns';
import i18next from 'i18next';

import { createURL } from '../../../utils/urlFormatting';
import { getDataOrThrow } from '../../../utils/api';
import type { GetPlaylistParams, Playlist, PlaylistItem, Source } from '../../../../types/playlist';
import env from '../../../env';
import ApiContentService from '../../ContentApiService';
import type { Config } from '../../../../types/config';
import { EPG_TIMELINE_DAYS_AFTER, EPG_TIMELINE_DAYS_BEFORE } from '../../../constants';
import { MediaStatus } from '../../../utils/liveEvent';
import type { EpisodeInSeries } from '../../../../types/series';
import AccountService from '../AccountService';

import type {
  ChannelItem,
  LineUpEventsResponse,
  LineupItemsResponse,
  List,
  ListItem,
  ListItemResource,
  ListItemsResponse,
  ListsResponse,
  Product,
  RecommendationItemsResponse,
  SearchResponse,
  ShowSeason,
} from './types';
import { fetchWithFingerprint, findImage } from './utils';
import StreamIQData from './StreamIQData';
import StreamIQApiService from './StreamIQApiService';
import StreamIQAccountService from './StreamIQAccountService';

const contentTypeMap: Record<string, string> = {
  show: 'Series',
  channel: 'LiveChannel',
  episode: 'Episode',
  event: 'LiveProgram',
  'tv-show': 'LiveSeries', // this fetches the latest episode event
};

@injectable()
export default class StreamIQContentApiService extends ApiContentService {
  constructor(
    @inject(StreamIQData) protected readonly data: StreamIQData,
    @inject(StreamIQApiService) protected readonly apiService: StreamIQApiService,
    @inject(AccountService) @named('STREAMIQ') protected readonly accountService: StreamIQAccountService,
  ) {
    super();
  }

  // the Stream IQ platform has no platform specific source rules (no SSAI, no user_id analytics param)
  protected override processSource(source: Source): Source {
    return source;
  }

  transformListItem = (listItem: ListItem, playlist?: Playlist, products?: Product[]): PlaylistItem => {
    const type = listItem.serviceName === 'epg-service' && listItem.type === 'show' ? `tv-show` : listItem.type;
    const contentType = contentTypeMap[type] || 'Movie';
    const startTime = listItem.startTime ? new Date(listItem.startTime) : undefined;
    const endTime = listItem.endTime ? new Date(listItem.endTime) : undefined;
    const mediaStatus =
      startTime && endTime
        ? isPast(startTime) && isFuture(endTime)
          ? MediaStatus.LIVE
          : isPast(endTime)
          ? MediaStatus.VOD
          : MediaStatus.SCHEDULED
        : MediaStatus.VOD; // @TODO we assume non-time based media items to be "VOD", but this might not be the case

    const trailerId =
      listItem.serviceName !== 'epg-service' && listItem.type !== 'channel' && listItem.type !== 'promotional'
        ? `promotional_${listItem.type}_${listItem.id}_${listItem.serviceName}`
        : undefined;
    const description = listItem.longDescription || listItem.description || '';
    // this item probably has a linked product, but we're not 100% sure (only used for showing the lock in shelves)
    const optimisticProducts = this.data.getProducts().filter((product) => product.productTemplateId === listItem.id);
    const mediaProducts = products ? products : optimisticProducts;

    const playlistItem: PlaylistItem = {
      contentType,
      feedid: playlist?.feedid || '',
      mediaid: `${type}_${listItem.id}`,
      genre: listItem.genres?.join(', ') || '',
      duration: typeof listItem.runtime === 'number' ? listItem.runtime * 60 : typeof listItem.duration === 'number' ? listItem.duration / 1000 : 0,
      title: listItem.title || listItem.name, // channel uses `name`
      description,
      cardImage: findImage(listItem.images, !playlist?.cardImageAspectRatio || playlist?.cardImageAspectRatio === '16:9' ? 'landscape' : 'portrait', 'medium'),
      channelLogoImage: findImage(listItem.images, 'landscape', 'medium'),
      backgroundImage: findImage(listItem.images, 'landscape', 'large', 'backdrop'),
      rating:
        listItem.parentalRatings
          ?.filter(({ system }) => system === 'NICAM')
          .map(({ rating }) => rating)
          .join(',') || '',
      directors:
        listItem.credits
          ?.filter((c) => c.role === 'Director' && !/^\d*$/.test(c.name))
          .slice(0, 3)
          .map((c) => c.name)
          .join(', ') || '',
      cast:
        listItem.credits
          ?.filter((c) => c.role === 'Actor' && !/^\d*$/.test(c.name))
          .slice(0, 5)
          .map((c) => c.name)
          .join(', ') || '',
      mediaOffers: mediaProducts.length ? mediaProducts.map((product) => ({ id: product.id, premier: true })) : undefined,
      episodeNumber: typeof listItem.episodeNumber === 'number' ? String(listItem.episodeNumber) : undefined,
      seasonNumber: typeof listItem.seasonNumber === 'number' ? String(listItem.seasonNumber) : undefined,
      scheduledStart: startTime,
      scheduledEnd: endTime,
      mediaStatus,
      showId: listItem.showId ? `show_${listItem.showId}` : undefined,
      series_id: listItem.showId && listItem.type === 'episode' ? `show_${listItem.showId}` : undefined,
      trailerId,
      sources: [],
      pubdate: listItem.releaseDate ? new Date(listItem.releaseDate).getTime() / 1000 : 0,
      tracks: [],
      image: '',
      link: '',
      images: [],
    };

    if (listItem.type === 'channel') {
      const channelItem = listItem as ChannelItem;
      playlistItem.catchupHours =
        channelItem.tstvConfig?.catchUp && channelItem.tstvConfig?.catchUpDurationInDays ? String(channelItem.tstvConfig.catchUpDurationInDays * 24) : '0';
    }

    const lineup = this.data.getLineup();
    if (lineup) {
      playlistItem.lineupId = `lineup_${lineup.id}`;
      playlistItem.liveChannelsId = `lineup_${lineup.id}`;
    }

    if (listItem.type === 'event' && listItem.channelId) {
      playlistItem.liveChannelsId = `channel_${listItem.channelId}`;
    }

    return playlistItem;
  };

  protected transformList = (list: List, listItems: ListItemResource[]) => {
    let custom: Record<string, unknown> = {};
    try {
      custom = list.customVariable ? (JSON.parse(list.customVariable) as Record<string, unknown>) : {};
    } catch {
      // no-op
    }

    const customCardAspectRatio = typeof custom?.cardImageAspectRatio === 'string' ? custom.cardImageAspectRatio : undefined;
    const defaultCardImageAspectRatio = custom?.isCardOrientationPriorityHorizontal === false ? '9:13' : '16:9';
    const cardImageAspectRatio = customCardAspectRatio || defaultCardImageAspectRatio;

    const playlist: Playlist = {
      title: list.name,
      description: '',
      playlist: [],
      cardImageAspectRatio,
      feedid: list.id,
    };

    playlist.playlist = listItems.map((listItem) => this.transformListItem(listItem.resource, playlist));

    return playlist;
  };

  protected getListGroupHub = async (id: string) => {
    const listGroupUrl = createURL(`${env.APP_API_BASE_URL}/layout-service/lists`, { listGroupId: id });
    const listGroupResponse = await fetchWithFingerprint(listGroupUrl);
    const lists = (await getDataOrThrow(listGroupResponse)) as ListsResponse;

    return {
      mediaid: id,
      feedid: '',
      contentType: 'Hub',
      title: '',
      description: '',
      playlists: lists.resources.map(({ id }) => id).join(','),
      duration: 0,
      sources: [],
      pubdate: 0,
      image: '',
      link: '',
      images: [],
    } as PlaylistItem;
  };

  protected getEpgLineupItems = async (id: string) => {
    const lineupResponse = await fetchWithFingerprint(`${env.APP_API_BASE_URL}/epg-service/lineups/${id}`);
    const lineup = (await getDataOrThrow(lineupResponse)) as { id: string; name: string };
    const lineupItemsUrl = createURL(`${env.APP_API_BASE_URL}/epg-service/lineupItems`, { lineupId: id, resolveReferences: 'true' });
    const listGroupResponse = await fetchWithFingerprint(lineupItemsUrl);
    const lists = (await getDataOrThrow(listGroupResponse)) as LineupItemsResponse;

    return {
      feedid: `lineup_${id}`,
      contentType: 'live',
      title: lineup.name,
      description: '',
      playlist: lists.resources
        .filter((item) => 'resource' in item)
        .map(({ resource: item }) => {
          const catchupHours = item.tstvConfig?.catchUp && item.tstvConfig?.catchUpDurationInDays ? item.tstvConfig.catchUpDurationInDays * 24 : 0;

          return {
            contentType: 'live',
            feedid: `lineup_${id}` || '',
            mediaid: `${item.type}_${item.id}`,
            genre: item.genres?.join(', ') || '',
            duration: 0,
            title: item.name,
            description: item.description || '',
            cardImage: findImage(item.images, 'landscape', 'medium'),
            channelLogoImage: findImage(item.images, 'landscape', 'medium'),
            backgroundImage: findImage(item.images, 'landscape', 'large', 'backdrop'),
            catchupHours: `${catchupHours}`,
            scheduleType: 'streamiq',
            scheduleUrl: item.id,
            mediaOffers: undefined,
            sources: [],
            pubdate: 0,
            tracks: [],
            image: '',
            link: '',
            images: [],
          } as PlaylistItem;
        }),
    } as Playlist;
  };

  /**
   * This fetches episode events for the given showId and returns a single episode that is either
   * - Currently live episode
   * - Most recent VOD episode
   * - Next scheduled episode
   */
  protected getEpgShowEpisodeEvent = async (showId: string) => {
    const pageSize = 999;
    const pathname = createURL('epg-service/events', {
      showId,
      pageSize,
      page: 0,
      startTime: Date.now() - 3600 * 24 * EPG_TIMELINE_DAYS_BEFORE * 1000,
      endTime: Date.now() + 3600 * 24 * EPG_TIMELINE_DAYS_AFTER * 1000,
    });

    const episodesResponse = await fetchWithFingerprint(`${env.APP_API_BASE_URL}/${pathname}`);
    const episodes = (await getDataOrThrow(episodesResponse)) as { resources: ListItem[] };
    const transformedEpisodes = episodes.resources.sort((a, b) => (a.startTime || 0) - (b.startTime || 0)).map((item) => this.transformListItem(item));

    const liveEpisode = transformedEpisodes.find((episode) => episode.mediaStatus === MediaStatus.LIVE);
    const latestVodEpisode = [...transformedEpisodes].reverse().find((episode) => episode.mediaStatus === MediaStatus.VOD);
    const firstScheduledEpisode = transformedEpisodes.find((episode) => episode.mediaStatus === MediaStatus.SCHEDULED);
    const targetEpisode = liveEpisode || latestVodEpisode || firstScheduledEpisode;

    if (!targetEpisode) throw new Error('No episode found for EPG series');

    return targetEpisode;
  };

  getPromotionalForItem = async (id: string) => {
    const [referenceType, referenceId, referenceServiceName = 'vod-service'] = id.split('_');

    const url = createURL(`${env.APP_API_BASE_URL}/vod-service/promotionals`, { referenceId, referenceType, referenceServiceName });
    const response = await fetchWithFingerprint(url);
    const list = (await getDataOrThrow(response)) as { resources: ListItem[] };
    const mediaItem = list.resources[0];

    if (!mediaItem) throw new Error('MediaItem not found');

    return {
      ...this.transformListItem(mediaItem),
      mediaid: `promotional_${id}`,
    };
  };

  /**
   * Get media by id
   */
  getMediaById = async ({ id, token }: { id: string; token?: string; config: Config }): Promise<PlaylistItem> => {
    const streamIqResource = id.split('_')[0];
    const streamIqId = id.split('_').slice(1).join('_');

    if (streamIqResource === 'listgroup') {
      return this.getListGroupHub(streamIqId);
    }

    if (streamIqResource === 'promotional') {
      return this.getPromotionalForItem(streamIqId);
    }

    const pathname = {
      movie: `/vod-service/movies/${streamIqId}`,
      show: `/vod-service/shows/${streamIqId}`,
      episode: `/vod-service/episodes/${streamIqId}`,
      channel: `/epg-service/channels/${streamIqId}`,
      'tv-show': `/epg-service/shows/${streamIqId}`,
      event: `/epg-service/events/${streamIqId}`,
      movieEvent: `/epg-service/events/${streamIqId}`,
      episodeEvent: `/epg-service/events/${streamIqId}`,
      showEvent: `/epg-service/events/${streamIqId}`,
    }[streamIqResource];

    if (!pathname) throw new Error(`Service not found for id ${id}`);

    const url = createURL(`${env.APP_API_BASE_URL}${pathname}`, { token });
    const response = await fetchWithFingerprint(url);
    const mediaItem = (await getDataOrThrow(response)) as ListItem;

    if (!mediaItem) throw new Error('MediaItem not found');

    const products = ['movie', 'show', 'episode'].includes(streamIqResource)
      ? (
          await this.apiService.get<{ resources: Product[] }>(
            `/crm-service/products?referenceServiceName=vod-service&referenceType=${streamIqResource}&referenceId=${streamIqId}&regionId=${this.accountService.regionId}`,
          )
        ).resources
      : [];

    return this.transformListItem(mediaItem, undefined, products);
  };

  protected getLiveEventForChannel = async (channelId: string) => {
    const now = Date.now();
    const response = await fetchWithFingerprint(
      createURL(`${env.APP_API_BASE_URL}/epg-service/events`, {
        channelId: channelId,
        startTime: now - 3600 * 1000,
        endTime: now + 3600 * 1000,
      }),
    );
    const data = (await getDataOrThrow(response)) as LineUpEventsResponse;
    const currentEvent = data.resources.find((event) => event.startTime < now && event.endTime > now);

    return currentEvent || data.resources[0];
  };

  protected getRecommendations = async ({ seedId }: { seedId: string; config: Config }) => {
    let streamIqResource = seedId.split('_')[0];
    let streamIqId = seedId.split('_').slice(1).join('_');

    if (streamIqResource === 'channel') {
      const channelEvent = await this.getLiveEventForChannel(streamIqId);

      if (channelEvent) {
        streamIqResource = channelEvent.type;
        streamIqId = channelEvent.id;
      }
    }

    const isEpgService = ['event', 'episodeEvent', 'movieEvent', 'tv-show'].includes(streamIqResource);
    const recommendationsUrl = createURL(`${env.APP_API_BASE_URL}/recommendation-service/recommendations`, {
      seedServiceName: isEpgService ? 'epg-service' : 'vod-service',
      seedResourceType: streamIqResource === 'tv-show' ? 'show' : streamIqResource,
      seedResourceId: streamIqId,
      recommendationType: 'moreLikeThis',
      resolveReferences: 'true',
    });
    const response = await fetchWithFingerprint(recommendationsUrl);
    const data = (await getDataOrThrow(response)) as RecommendationItemsResponse;

    const items = data.resources.filter((item) => 'resource' in item).map((item) => this.transformListItem(item.resource));

    const playlist = {
      id: 'recommendations',
      title: i18next.t('video:recommended'),
      playlist: items,
    } as Playlist;

    return playlist;
  };

  getPlaylistById = async ({ id, params = {}, config }: { id?: string; params?: GetPlaylistParams; config: Config }): Promise<Playlist> => {
    if (!id) {
      throw new Error('id is missing');
    }

    if (id === 'recommendations' && params.related_media_id) {
      return this.getRecommendations({ seedId: params.related_media_id, config });
    }

    const [prefix, playlistId] = id.split('_');

    // playlist menu item labelled "TV Guide" is forced to be an EPG page
    if (playlistId && prefix === 'lineup') {
      return this.getEpgLineupItems(playlistId);
    }

    const listUrl = `/layout-service/lists/${id}`;
    const listData = await this.apiService.get<List>(listUrl, { withAuthentication: true });

    let listItemsData: ListItemsResponse;

    if (listData.query) {
      const listItemsQueryUrl = new URL(listData.query, env.APP_API_BASE_URL);

      listItemsData = await this.apiService.get<ListItemsResponse>(
        listItemsQueryUrl.pathname,
        { withAuthentication: true, headers: { 'X-Stream-Iq-Profile-Id': this.accountService.profileId ?? '' } },
        {
          ...Object.fromEntries(listItemsQueryUrl.searchParams.entries()),
          resolveReferences: 'true', // this param is not present in the `List.query`
        },
      );
    } else {
      listItemsData = await this.apiService.get<ListItemsResponse>(
        '/layout-service/listItems',
        { withAuthentication: true },
        { ...params, listId: id, resolveReferences: 'true' },
      );
    }

    return this.transformList(
      listData,
      listItemsData.resources.filter((item) => 'resource' in item),
    );
  };

  getSeries = async ({ id }: { id: string }) => {
    const streamIqId = id.split('_').slice(1).join('_');
    const seasonsResponse = await fetchWithFingerprint(`${env.APP_API_BASE_URL}/vod-service/seasons?showId=${streamIqId}`);
    const seasons = (await getDataOrThrow(seasonsResponse)) as { resources: ShowSeason[] };

    const episodesResponse = await this.getEpisodes({ seriesId: id, pageLimit: 999 });
    const totalDuration = episodesResponse.episodes.reduce((previousValue, currentValue) => {
      return previousValue + currentValue.duration;
    }, 0);

    return {
      series_id: id,
      episode_count: episodesResponse.episodes.length,
      season_count: seasons.resources.length,
      total_duration: totalDuration,
      seasons: seasons.resources
        .sort((a, b) => a.seasonNumber - b.seasonNumber)
        .map((season) => ({
          season_id: season.id,
          season_number: season.seasonNumber,
          season_title: '',
          season_description: season.description,
          episode_count: episodesResponse.episodes.filter((item) => item.seasonId === season.id).length,
          total_duration: episodesResponse.episodes
            .filter((item) => item.seasonId === season.id)
            .reduce((previousValue, currentValue) => {
              return previousValue + currentValue.duration;
            }, 0),
        })),
    };
  };

  getEpisodes = async ({
    seriesId,
    afterId,
    pageOffset = 0,
    pageLimit = 999,
    seasonNumber,
  }: {
    seriesId: string | undefined;
    afterId?: string;
    seasonNumber?: number;
    pageOffset?: number;
    pageLimit?: number;
  }) => {
    const pageSize = 999;
    const [streamIqResource, streamIqId] = seriesId?.split('_') || [];
    const pathname =
      streamIqResource === 'show'
        ? createURL('vod-service/episodes', { showId: streamIqId, pageSize, page: pageOffset })
        : createURL('epg-service/events', {
            showId: streamIqId,
            pageSize,
            page: pageOffset,
            startTime: Date.now() - 3600 * 24 * EPG_TIMELINE_DAYS_BEFORE * 1000,
            endTime: Date.now() + 3600 * 24 * EPG_TIMELINE_DAYS_AFTER * 1000,
          });

    const episodesResponse = await fetchWithFingerprint(`${env.APP_API_BASE_URL}/${pathname}`);
    const episodes = (await getDataOrThrow(episodesResponse)) as { resources: ListItem[] };

    const filteredEpisodes = episodes.resources.filter((episode) => {
      if (typeof seasonNumber !== 'undefined' && seasonNumber !== 0) return episode.seasonNumber === seasonNumber;
      return true;
    });

    let sortedEpisodes;

    if (streamIqResource === 'tv-show') {
      const now = Date.now();

      sortedEpisodes = filteredEpisodes.sort((a, b) => Math.abs((a.startTime ?? 0) - now) - Math.abs((b.startTime ?? 0) - now));
    } else {
      sortedEpisodes = filteredEpisodes.sort((a, b) => {
        if (a.seasonNumber !== b.seasonNumber) {
          return Number(a.seasonNumber) - Number(b.seasonNumber);
        }
        return Number(a.episodeNumber) - Number(b.episodeNumber);
      });
    }

    // virtual filter (for resolving the next episode)
    const afterEpisodeId = afterId?.split('_')[1];
    if (afterEpisodeId) {
      sortedEpisodes = sortedEpisodes.slice(sortedEpisodes.findIndex((item) => item.id === afterEpisodeId) + 1);
    }

    // virtual pagination due to sorting is missing in the StreamIQ API, we can't resolve the first episode without
    // requesting them all
    if (pageLimit !== 999) {
      sortedEpisodes = sortedEpisodes.slice(0, pageLimit);
    }

    return {
      episodes: sortedEpisodes.map((item) => this.transformListItem(item)),
      pagination: {
        page: pageOffset + 1,
        page_limit: pageLimit,
        total: sortedEpisodes.length,
      },
    };
  };

  getSeriesByMediaIds = async ({ mediaIds, config }: { mediaIds: string[]; config: Config }) => {
    const bookmarkDictionary = Object.fromEntries(
      this.data.getBookmarks().map((bookmark) => [`${bookmark.resource.type}_${bookmark.resource.id}`, this.transformListItem(bookmark.resource)]),
    );

    const result: Record<string, EpisodeInSeries[]> = {};

    for (const mediaId of mediaIds) {
      const listItem = bookmarkDictionary[mediaId] || (await this.getMediaById({ id: mediaId, config }));

      result[mediaId] =
        typeof listItem.showId === 'string'
          ? [{ series_id: listItem.showId, episode_number: Number(listItem.episodeNumber || '1'), season_number: Number(listItem.seasonNumber || '1') }]
          : [];
    }

    return result;
  };

  search = async ({ searchTerm }: { searchTerm: string; config: Config }) => {
    const url = createURL(`${env.APP_API_BASE_URL}/search-service/resources`, { search: searchTerm, resolveReferences: 'true' });
    const response = await fetchWithFingerprint(url);
    const data = (await getDataOrThrow(response)) as SearchResponse;

    const items = data.resources
      .filter(({ resource }) => !!resource) // Item reference was not resolved
      .filter(({ resource }) => resource.serviceName !== 'epg-service' || !resource.showId)
      .map(({ resource }) => this.transformListItem(resource));

    const playlist = {
      id: 'search',
      title: 'Search results',
      playlist: items,
    } as Playlist;

    return playlist;
  };

  getFavoriteMediaItems = async ({ ids }: { ids: string[]; config: Config; language?: string; token?: string }) => {
    const favorites = this.data.getFavorites().map((item) => this.transformListItem(item.resource));
    const favoritesDictionary = Object.fromEntries(favorites.map((listItem) => [listItem.mediaid, listItem]));

    return ids.map((id) => favoritesDictionary[id]).filter(Boolean);
  };

  getWatchHistoryMediaItems = async ({ ids, config }: { ids: string[]; config: Config }) => {
    const bookmarks = this.data.getBookmarks().map((item) => this.transformListItem(item.resource));
    const bookmarksDictionary = Object.fromEntries(bookmarks.map((listItem) => [listItem.mediaid, listItem]));

    const settledResults = await Promise.allSettled(
      ids.map((id) => {
        if (bookmarksDictionary[id]) return bookmarksDictionary[id];

        return this.getMediaById({ id, config });
      }),
    );

    return settledResults.map((settledResult) => (settledResult.status === 'fulfilled' ? settledResult.value : undefined)).filter((item) => !!item);
  };
}
